import React from 'react';
import dayjs from 'dayjs';
import {
  Box, Paper, TextField, MenuItem, Button,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, Stack, Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import Client from '../Client';
import Config from '../ClientConfig';

const compactFieldSx = {
  minWidth: {
    xs: 'calc(50% - 6px)',
    sm: 140,
  },
  flex: {
    xs: '1 1 calc(50% - 6px)',
    sm: '0 0 auto',
  },
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};

const compactFilterPaperSx = {
  flexShrink: 0,
  p: {
    xs: 0.5,
    sm: 0.75,
  },
  mb: 0.75,
  borderRadius: '14px',
  overflow: 'hidden',
};

const compactTablePaperSx = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '14px',
};

const compactTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  width: '100%',
};

const compactTableSx = {
  minWidth: {
    xs: 980,
    sm: 1080,
    md: '100%',
  },
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.45, sm: 0.65 },
    px: { xs: 0.5, sm: 0.9 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
    whiteSpace: 'nowrap',
  },
};

const compactPaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

const escapeCsv = (value) => {
  const text = (value ?? '').toString();
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const fixedOrEmpty = (value, digits = 2) => (
  value === null || value === undefined || isNaN(Number(value))
    ? ''
    : Number(value).toFixed(digits)
);

export function buildInventoryMonthCloseCsv(rows = []) {
  const header = [
    'Period', 'Location', 'Location_relation',
    'Product', 'Category', 'Subcategory', 'Product_relation',
    'Initial Qty', 'Initial Value',
    'IN Qty', 'IN Cost',
    'OUT Qty', 'OUT Cost',
    'Final Qty', 'Final Value'
  ];

  const lines = rows.map(row => ([
    escapeCsv(row.close_period),
    escapeCsv(row.locationName),
    escapeCsv(row.locationRelation),
    escapeCsv(row.productName),
    escapeCsv(row.categoryName),
    escapeCsv(row.subcategoryName),
    escapeCsv(row.productRelation),
    fixedOrEmpty(row.initialQty),
    fixedOrEmpty(row.initialValue),
    fixedOrEmpty(row.inQty),
    fixedOrEmpty(row.inCost),
    fixedOrEmpty(row.outQty),
    fixedOrEmpty(row.outCost),
    fixedOrEmpty(row.finalQty),
    fixedOrEmpty(row.finalValue),
  ].join(',')));

  return '\uFEFF' + [header.join(','), ...lines].join('\r\n');
}

class InventoryMonthClose extends React.Component {
  constructor(props) {
    super(props);

    const thisMonth = dayjs().format('YYYY-MM');

    this.state = {
      client: new Client('none'),
      loading: false,

      // lookups
      locations: [],
      products: [],
      productNameMap: {},
      productRelationMap: {},

      // filters sent to backend
      filters: {
        period: thisMonth,  // YYYY-MM
        location: '',       // location iref
        product: '',        // client-side only
      },

      // data
      all_rows: [],        // full flattened list for local product filtering
      rows: [],            // currently displayed rows

      // ui
      page: 0,
      rowsPerPage: -1,
    };
  }

  componentDidMount() {
    this.fetchLookups();
  }

  // ---------- utils ----------
  nf = (val, digits = 2) =>
    val == null || val === '' ? '' : Number(val).toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  getLocationName = (locId) => {
    if (!locId) return '';
    const l = (this.state.locations || []).find(ll =>
      String(ll.iref || ll.id || ll.uid) === String(locId)
    );
    return l ? (l.name || l.description || String(locId)) : String(locId);
  };

  getProductName = (prodId) => {
    if (!prodId) return '';
    const map = this.state.productNameMap || {};
    return map[String(prodId)] || String(prodId);
  };

  getProductRelation = (prodId) => {
    if (!prodId) return '';
    const map = this.state.productRelationMap || {};
    return map[String(prodId)] || String(prodId);
  };

  applyProductFilter(rows, productId) {
    if (!productId) return rows;
    return (rows || []).filter(r => String(r.productId) === String(productId));
  }

  // ---------- lookups ----------
  fetchLookups() {
    this.setState({ loading: true });

    const locationsResp = this.state.client.getData(Config.API_URL_LOCATIONS + 'list_locations/', { status: 1 }) || {};
    const productsResp  = this.state.client.getData(Config.API_URL_PRODUCT + 'list_products/', { status: 2, orderBy_name: true }) || {};

    const locations = Array.isArray(locationsResp.locations) ? locationsResp.locations : [];
    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const products  = Array.isArray(productsResp.products) ? productsResp.products : [];

    const productNameMap = {};
    products.forEach((p) => {
      const pid = p.iref;
      const pname = p.name;
      if (pid) productNameMap[String(pid)] = String(pname);
    });

    const productRelationMap = {};
    products.forEach((p) => {
      const pid = p.iref;
      const prelation = p.relation;
      if (pid) productRelationMap[String(pid)] = String(prelation);
    });

    this.setState({ locations, products, productNameMap, productRelationMap, loading: false }, () => {
      this.fetchData(); // initial load with defaults
    });
  }

  // ---------- build query for backend ----------
  buildQuery() {
    const { period, location } = this.state.filters;
    const [yearStr, monthStr] = (period || '').split('-');
    const q = {
      period,
      year: yearStr,
      month: monthStr,
    };
    if (location) q.location = location;
    // product is client-side only
    return q;
  }

  // ---------- fetch ----------
  fetchData() {
    const { client, filters } = this.state;
    this.setState({ loading: true, page: 0 });

    const query = this.buildQuery();

    // Correct endpoint constant
    const resp = client.getData(Config.API_URL_REPORTS_MONTH_CLOSE, query) || {};
    const list = Array.isArray(resp.inventoryClose) ? resp.inventoryClose : [];

    // Flatten lines (use server-computed fields)
    const flattened = [];
    list.forEach((close) => {
      const locId = close.location?.iref || '';
      const locName = close.location?.name || '';
      const locRelation = close.location?.extRelation || '';
      const lines = Array.isArray(close.inventoryCloseLines_invClose) ? close.inventoryCloseLines_invClose : [];
      lines.forEach((ln) => {
        flattened.push({
          id: ln.iref || `${close.iref}-${ln.product}`,
          close_period: this.state.filters.period,
          locationId: String(locId || ''),
          locationName: locName,
          locationRelation: locRelation,
          productId: String(ln.product || ''),
          productName: ln.productName || this.getProductName(ln.product),
          categoryName: ln.categoryName || '',
          subcategoryName: ln.subcategoryName || '',
          productRelation: ln.productRelation || this.getProductRelation(ln.product),
          initialQty: Number(ln.initialQty || 0),
          initialValue: Number(ln.initialValue || 0),
          inQty: Number(ln.inQty || 0),
          inCost: Number(ln.inCost || 0),
          outQty: Number(ln.outQty || 0),
          outCost: Number(ln.outCost || 0),
          finalQty: Number(ln.finalQty != null ? ln.finalQty : (ln.quantity || 0)),
          finalValue: Number(ln.finalValue != null ? ln.finalValue : ((ln.quantity || 0) * (ln.price || 0))),
        });
      });
    });

    // Apply client-side product filter
    const rows = this.applyProductFilter(flattened, filters.product);

    this.setState({
      all_rows: flattened,
      rows,
      loading: false,
    });
  }

  // ---------- events ----------
  setFilter = (name, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [name]: value } }));
  };

  onApply = () => this.fetchData();

  onReset = () => {
    const thisMonth = dayjs().format('YYYY-MM');
    this.setState({
      filters: { period: thisMonth, location: '', product: '' },
      page: 0,
    }, () => this.fetchData());
  };

  onChangePage = (_e, newPage) => this.setState({ page: newPage });
  onChangeRowsPerPage = (e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 });

  // ---------- CSV export ----------
  exportCSV = () => {
    const { rows, filters } = this.state;
    const csv = buildInventoryMonthCloseCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const periodSafe = (filters.period || 'period').replace(/[^0-9-]/g, '');
    const fname = `InventoryMonthClose_${periodSafe}.csv`;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------- chips ----------
  renderChips() {
    const f = this.state.filters;
    const chips = [];

    if (f.period) chips.push({ label: `Period: ${f.period}` });
    if (f.location) chips.push({ label: `Location: ${this.getLocationName(f.location)}` });
    if (f.product) chips.push({ label: `Product: ${this.getProductName(f.product)}` });

    if (!chips.length) return null;

    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          mt: 0.75,
          flexWrap: 'wrap',
          '& .MuiChip-root': {
            height: 24,
            maxWidth: '100%',
          },
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {chips.map((c, i) => <Chip key={i} size="small" label={c.label} />)}
      </Stack>
    );
  }

  // ---------- render ----------
  renderFilters() {
    const { filters, locations, products } = this.state;

    const row = {
      display: 'flex',
      gap: { xs: 0.75, sm: 1 },
      flexWrap: 'wrap',
      alignItems: 'center',
      '& .MuiButton-root': compactButtonSx,
    };

    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Box sx={row}>
          {/* Period (year-month) */}
          <TextField
            label="Period"
            type="month"
            value={filters.period}
            onChange={(e) => this.setFilter('period', e.target.value)}
            size="small"
            margin="dense"
            sx={compactFieldSx}
            InputLabelProps={{ shrink: true }}
          />

          {/* Location */}
          <TextField
            select
            label="Location"
            value={filters.location}
            onChange={(e) => this.setFilter('location', e.target.value)}
            size="small"
            margin="dense"
            sx={compactFieldSx}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={String(loc.iref || loc.uid || loc.id)} value={String(loc.iref || loc.uid || loc.id)}>
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          {/* Actions */}
          <Button variant="outlined" size="small" onClick={this.onReset}>
            Reset
          </Button>
          <Button variant="contained" size="small" onClick={this.onApply}>
            Apply
          </Button>

          {/* Product (client-side, searchable) */}
          <Autocomplete
            size="small"
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '0 0 260px',
              },
              minWidth: 0,
              maxWidth: {
                xs: '100%',
                sm: 340,
              },
              '& .MuiInputBase-root': {
                minHeight: 34,
                borderRadius: '12px',
              },
              '& .MuiInputBase-input': {
                fontSize: {
                  xs: '16px',
                  sm: '0.84rem',
                },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              },
            }}
            disablePortal
            options={[
              { label: 'All', value: '' },
              ...(products || []).map(p => ({
                value: String(p.iref || p.id || p.uid || p.code),
                label:
                  p.name ||
                  p.productName ||
                  p.description ||
                  p.code ||
                  String(p.iref || p.id || p.uid || p.code),
              })),
            ]}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o?.label ?? '')}
            value={(() => {
              const v = filters.product || '';
              if (!v) return { label: 'All', value: '' };
              const p = (products || []).find(pp =>
                String(pp.iref || pp.id || pp.uid || pp.code) === String(v)
              );
              return p
                ? {
                    value: String(p.iref || p.id || p.uid || p.code),
                    label:
                      p.name ||
                      p.productName ||
                      p.description ||
                      p.code ||
                      String(v),
                  }
                : { label: 'All', value: '' };
            })()}
            isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
            onChange={(_e, opt) => {
              const newVal = opt ? opt.value : '';
              this.setFilter('product', newVal);
              const filtered = this.applyProductFilter(this.state.all_rows || [], newVal);
              this.setState({ rows: filtered, page: 0 });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                margin="dense"
                // no -webkit-box / line-clamp here!
                inputProps={{
                  ...params.inputProps,
                  title: params.inputProps?.value, // show full on hover
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} title={option.label}>
                <div
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2, // clamp only in the dropdown
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    maxWidth: 320,
                  }}
                >
                  {option.label}
                </div>
              </li>
            )}
          />
          <Button variant="outlined" size="small" onClick={this.exportCSV}>
            CSV
          </Button>
        </Box>

        {this.renderChips()}
      </Paper>
    );
  }

  renderTable() {
    const { loading, rows, page, rowsPerPage } = this.state;
    const paged = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Initial Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Initial Value</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">IN Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">IN Cost</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">OUT Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">OUT Cost</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Final Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Final Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((row, idx) => (
                <TableRow key={row.id || `${page}-${idx}`} hover>
                  <TableCell>{row.locationName}</TableCell>
                  <TableCell>
                    <Tooltip title={row.productId} placement="top" arrow>
                      <span>{row.productName}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{this.nf(row.initialQty)}</TableCell>
                  <TableCell align="right">{this.nf(row.initialValue)}</TableCell>
                  <TableCell align="right">{this.nf(row.inQty)}</TableCell>
                  <TableCell align="right">{this.nf(row.inCost)}</TableCell>
                  <TableCell align="right">{this.nf(row.outQty)}</TableCell>
                  <TableCell align="right">{this.nf(row.outCost)}</TableCell>
                  <TableCell align="right">{this.nf(row.finalQty)}</TableCell>
                  <TableCell align="right">{this.nf(row.finalValue)}</TableCell>
                </TableRow>
              ))}

              {!paged.length && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">No data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={this.onChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={this.onChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={compactPaginationSx}
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}
        {this.renderTable()}
      </Box>
    );
  }
}

export default InventoryMonthClose;
